"use client";

import { Link } from "react-router-dom";
import { useRef } from "react";
import { SignOutButton } from "@/components/account/SignOutButton";

export type CrshmToolMenuActive =
  | "waitlist"
  | "dod"
  | "blog"
  | "dashboard"
  | "admin"
  | "signin"
  | "profile"
  | "orders";

export type CrshmToolMenuVariant = "light" | "dark";

type Props = {
  active: CrshmToolMenuActive;
  variant: CrshmToolMenuVariant;
  email?: string | null;
  showAdminLink?: boolean;
};

export function CrshmToolMenu({ active, variant, email, showAdminLink }: Props) {
  const detailsRef = useRef<HTMLDetailsElement>(null);

  const close = () => {
    const el = detailsRef.current;
    if (el) el.open = false;
  };

  const triggerSkin =
    variant === "light"
      ? "text-zinc-800 hover:bg-zinc-900/[0.06] active:bg-zinc-900/[0.1]"
      : "text-zinc-100 hover:bg-white/[0.08] active:bg-white/[0.12]";

  const panelSkin =
    variant === "light"
      ? "border border-violet-200/90 bg-[#faf8ff]/98 text-zinc-900 shadow-lg backdrop-blur-lg"
      : "border border-white/15 bg-zinc-950/98 text-zinc-100 shadow-lg backdrop-blur-lg";

  const linkInactive =
    variant === "light"
      ? "text-zinc-700 hover:bg-violet-50 active:bg-violet-100"
      : "text-zinc-300 hover:bg-white/10 active:bg-white/15";

  const linkActive =
    variant === "light" ? "bg-violet-600 text-white" : "bg-fuchsia-600 text-white";

  const sep = variant === "light" ? "border-violet-100" : "border-white/10";

  const itemClass = (key: CrshmToolMenuActive) =>
    `block px-4 py-3 text-sm font-bold transition ${active === key ? linkActive : linkInactive}`;

  const signedIn = Boolean(email?.trim());

  return (
    <details ref={detailsRef} className="relative z-[100] shrink-0">
      <summary
        className={`flex size-10 cursor-pointer list-none items-center justify-center rounded-lg transition [&::-webkit-details-marker]:hidden ${triggerSkin}`}
        aria-label="Open navigation menu"
      >
        <span className="flex flex-col gap-[5px]" aria-hidden>
          <span className="block h-0.5 w-[1.15rem] rounded-full bg-current" />
          <span className="block h-0.5 w-[1.15rem] rounded-full bg-current" />
        </span>
      </summary>

      <nav
        className={`absolute right-0 top-full z-[110] mt-1.5 min-w-[12rem] overflow-hidden rounded-xl ${panelSkin}`}
        aria-label="Pages"
      >
        <Link
          to="/"
          className={itemClass("waitlist")}
          aria-current={active === "waitlist" ? "page" : undefined}
          onClick={close}
        >
          Waitlist
        </Link>
        <Link
          to="/do-or-dump"
          className={`border-t ${sep} ${itemClass("dod")}`}
          aria-current={active === "dod" ? "page" : undefined}
          onClick={close}
        >
          Do or Dump
        </Link>
        {!signedIn ? (
          <Link
            to="/blog"
            className={`border-t ${sep} ${itemClass("blog")}`}
            aria-current={active === "blog" ? "page" : undefined}
            onClick={close}
          >
            Blog
          </Link>
        ) : null}

        {signedIn ? (
          <>
            <Link
              to="/account/profile"
              className={`border-t ${sep} ${itemClass("profile")}`}
              aria-current={active === "profile" ? "page" : undefined}
              onClick={close}
            >
              Profile
            </Link>
            <Link
              to="/account/orders"
              className={`border-t ${sep} ${itemClass("orders")}`}
              aria-current={active === "orders" ? "page" : undefined}
              onClick={close}
            >
              Orders
            </Link>
            {showAdminLink ? (
              <Link
                to="/admin"
                className={`border-t ${sep} ${itemClass("admin")}`}
                aria-current={active === "admin" ? "page" : undefined}
                onClick={close}
              >
                Admin
              </Link>
            ) : null}
            <Link
              to="/blog"
              className={`border-t ${sep} ${itemClass("blog")}`}
              aria-current={active === "blog" ? "page" : undefined}
              onClick={close}
            >
              Blog
            </Link>
            <div className={`border-t ${sep} px-2 py-2`}>
              <SignOutButton
                className={`w-full rounded-lg px-3 py-2.5 text-left text-sm font-bold transition disabled:opacity-60 ${variant === "light" ? "text-zinc-800 hover:bg-violet-50" : "text-zinc-100 hover:bg-white/10"}`}
              />
            </div>
          </>
        ) : null}
      </nav>
    </details>
  );
}
